import * as vp from 'vopay';
import { FunctionComponent, useState, useEffect, JSX } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import queryString from 'query-string';
import useFileDownload from '../../../../../hooks/useFiledDownload';
import styles from '../../SubAccount.module.scss';
import { TransactionTypeLabel } from 'UI';

const { Box, SubTitle, Table, Title, Button, DropDown, Badge, formattedCurrency } = vp;

type TablePropsHandlers = {
  transactionDetails: () => vp.TableProps;
};

type KeyValuePair = {
  display: string;
  value: string | JSX.Element;
};

type DataProps = {
  transaction: vp.TransactionsLists;
};

type FC = FunctionComponent;

const TransactionDetails: FC = () => {
  const history = useHistory();
  const location = useLocation<DataProps>();
  const { state } = location;
  const transaction: vp.TransactionsLists = location.search
    ? (queryString.parse(location.search) as any as vp.TransactionsLists)
    : state.transaction;
  const { printFile, downloadPdfPage } = useFileDownload();
  const [isMenuVisible, setIsMenuVisible] = useState(false);
  const [isExportingPDF, setIsExportingPDF] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);

  useEffect(() => {
    if (isExportingPDF) {
      downloadPdfPage('transaction-overview', 'transaction-details');
    }
    if (isPrinting) {
      printFile('transactionDetailsTable');
    }
    setIsPrinting(false);
  }, [isExportingPDF, isPrinting]);

  return (
    <Box flx="col">
      <Box width="100%" flx="row" jc="sb" maxheight="43px">
        <Title title="Sub-Account Overview" />
        <Box flx="row" gap="sm">
          <Button
            id="view-transactions"
            label="View Transactions"
            onClick={() => {
              history.goBack();
            }}
          />
          <div className={styles.dropdownContainer}>
            <Button
              id="export-transactions"
              color="none"
              label="Export"
              height="42px"
              textSize={1}
              background="transparent"
              noShadow
              icon="arrowDown"
              iconLocation="right"
              onClick={() => {
                setIsMenuVisible(true);
              }}
            />
            {isMenuVisible && (
              <DropDown isMenuVisible={isMenuVisible} onClick={setIsMenuVisible}>
                <Button
                  id="export-PDF-btn"
                  label="PDF"
                  background="transparent"
                  textSize={1}
                  width="100%"
                  noShadow
                  onClick={() => {
                    setIsExportingPDF(true);
                  }}
                />
                <Button
                  id="export-Print-btn"
                  label="Print"
                  background="transparent"
                  textSize={1}
                  width="100%"
                  noShadow
                  onClick={() => {
                    setIsPrinting(true);
                  }}
                />
              </DropDown>
            )}
          </div>
        </Box>
      </Box>
      <Box flx="col" width="50%" mt="md">
        <div id="transaction-overview">
          <SubTitle subTitle="Transfer Overview" />
          <Box mb="md">
            <Table {...getTableProps('transactionDetails')} />
          </Box>
        </div>
      </Box>
    </Box>
  );

  function getTableProps(handler: keyof TablePropsHandlers): vp.TableProps {
    const handlers: TablePropsHandlers = {
      transactionDetails: () => {
        const TransactionDetailsPair: KeyValuePair[] = [
          {
            display: 'Transaction ID',
            value: transaction.TransactionID || '',
          },
          {
            display: 'Name',
            value: transaction.FullName || '',
          },
          {
            display: 'Transaction Date',
            value: transaction.TransactionDateTime || '',
          },
          {
            display: 'Last Modified',
            value: transaction.LastModified || '',
          },
          {
            display: 'Type',
            value: <TransactionTypeLabel transactionType={transaction.TransactionType || ''} />,
          },
          {
            display: 'Status',
            value: (
              <Badge
                label={vp.capitalizeFirstLetterOfWords(transaction.TransactionStatus || '')}
                hasIcon
                color={vp.GetColorStatus(transaction.TransactionStatus || '')}
              />
            ),
          },
          {
            display: 'Debit Amount',
            value: `$${formattedCurrency(transaction.DebitAmount)}` || '',
          },
          {
            display: 'Credit Amount',
            value: `$${formattedCurrency(transaction.CreditAmount)}` || '',
          },
          {
            display: 'Hold Amount',
            value: `$${formattedCurrency(transaction.HoldAmount)}` || '',
          },
          {
            display: 'Currency',
            value: transaction.Currency || '',
          },
          {
            display: 'Notes',
            value: transaction.Notes || '',
          },
        ];

        const transactionDetails: vp.TableProps = {
          id: 'transactionDetailsTable',
          name: 'transaction-details',
          variant: 'pair',
          columns: {
            detailKey: { header: 'Transaction Detail' },
            detailValue: { header: '' },
          },
          rows: TransactionDetailsPair.map(({ display, value }) => ({
            detailKey: display,
            detailValue: value,
          })),
        };

        return transactionDetails;
      },
    };

    return handlers[handler]();
  }
};

export default TransactionDetails;
